import { useEffect, useState } from "react";
import moment from "moment";

interface Countdown {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

let timerId: any;

const useTimer = (): [Countdown, (initialTimestamp: number) => void] => {
  const countdown: Countdown = { days: 0, hours: 0, minutes: 0, seconds: 0 };
  const [targetTimestamp, setTargetTimestamp] = useState<number | null>(null);
  const [initialized, setIsInitialized] = useState(false);
  const [timeLeft, setTimeLeft] = useState<Countdown>(countdown);

  const calculateTimeLeft = (): Countdown => {
    if (targetTimestamp === null) {
      return countdown; // If no timestamp is set, return default countdown
    }

    const now = moment();
    const target = moment.unix(targetTimestamp);
    const duration = moment.duration(target.diff(now));

    if (duration.asSeconds() <= 0) {
      setTimeLeft(countdown);
      clearInterval(timerId);
      return countdown;
    }

    return {
      days: Math.floor(duration.asDays()),
      hours: duration.hours(),
      minutes: duration.minutes(),
      seconds: duration.seconds(),
    };
  };

  useEffect(() => {
    if (initialized) {
      // Start the timer once initialTimestamp is set
      timerId = setInterval(() => {
        setTimeLeft(calculateTimeLeft());
        setTargetTimestamp((prevTimestamp) =>
          prevTimestamp ? prevTimestamp - 1 : 0
        ); // Decrease by 1 second
      }, 1000);
    }
    // eslint-disable-next-line
  }, [initialized]);

  const setInitialTimeStamp = (initialTimestamp: number) => {
    setTargetTimestamp(initialTimestamp);
    setIsInitialized(true);
  };

  useEffect(() => {
    // Cleanup on unmount or when targetTimestamp is null
    return () => {
      if (timerId) {
        clearInterval(timerId);
      }
    };
  }, []);

  return [timeLeft, setInitialTimeStamp];
};

export default useTimer;
